// src/components/notes/NotesEditor.tsx
import React, { useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { CodeNode, CodeHighlightNode } from "@lexical/code";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect } from "react";
import { BLUR_COMMAND, FOCUS_COMMAND, COMMAND_PRIORITY_LOW } from "lexical";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotesEditor } from "@/context/NotesEditorContext";
import { SlashCommandPlugin } from "./plugins/SlashCommandPlugin";
import { FloatingToolbarPlugin } from "./plugins/FloatingToolbarPlugin";

interface NotesEditorProps {
  initialText?: string;
}

function EditorRefPlugin() {
  const [editor] = useLexicalComposerContext();
  const { setEditor } = useNotesEditor();

  useEffect(() => {
    setEditor(editor);
    return () => setEditor(null);
  }, [editor, setEditor]);

  return null;
}

function FocusPlugin({ onFocus, onBlur }: { onFocus: () => void; onBlur: () => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      FOCUS_COMMAND,
      () => {
        onFocus();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onFocus]);

  useEffect(() => {
    return editor.registerCommand(
      BLUR_COMMAND,
      () => {
        onBlur();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onBlur]);

  return null;
}

const editorConfig = {
  namespace: "NotesEditor",
  theme: {
    paragraph: "mb-2",
    heading: {
      h1: "text-2xl font-bold mb-3",
      h2: "text-xl font-bold mb-2",
      h3: "text-lg font-bold mb-2",
    },
    list: {
      ul: "list-disc list-inside mb-2",
      ol: "list-decimal list-inside mb-2",
      checklist: "list-none mb-2",
      listitem: "mb-1",
      listitemChecked: "line-through text-muted-foreground",
      listitemUnchecked: "",
    },
    quote: "border-l-4 border-muted-foreground/30 pl-4 italic text-muted-foreground mb-2",
    code: "bg-muted rounded px-1.5 py-0.5 font-mono text-sm",
    codeHighlight: {
      atrule: "text-purple-500",
      attr: "text-blue-500",
      boolean: "text-orange-500",
      builtin: "text-cyan-500",
      cdata: "text-gray-500",
      char: "text-green-500",
      class: "text-yellow-500",
      "class-name": "text-yellow-500",
      comment: "text-gray-500",
      constant: "text-orange-500",
      deleted: "text-red-500",
      doctype: "text-gray-500",
      entity: "text-orange-500",
      function: "text-blue-500",
      important: "text-orange-500",
      inserted: "text-green-500",
      keyword: "text-purple-500",
      namespace: "text-red-500",
      number: "text-orange-500",
      operator: "text-cyan-500",
      prolog: "text-gray-500",
      property: "text-blue-500",
      punctuation: "text-gray-600",
      regex: "text-red-500",
      selector: "text-green-500",
      string: "text-green-500",
      symbol: "text-orange-500",
      tag: "text-red-500",
      url: "text-cyan-500",
      variable: "text-red-500",
    },
    horizontalRule: "my-4 border-t border-border",
    link: "text-primary underline cursor-pointer hover:text-primary/80",
  },
  nodes: [
    HeadingNode, 
    ListNode, 
    ListItemNode, 
    LinkNode, 
    AutoLinkNode, 
    QuoteNode, 
    CodeNode, 
    CodeHighlightNode,
    HorizontalRuleNode,
  ],
  onError: (error: Error) => {
    console.error("Lexical error:", error);
  },
};

export const NotesEditor: React.FC<NotesEditorProps> = ({ initialText }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="h-full bg-inherit">
      <LexicalComposer initialConfig={editorConfig}>
        <div className="relative h-full flex flex-col">
          <ScrollArea className="h-full">
            <div className="relative mx-auto w-full max-w-4xl">
                <RichTextPlugin
                  contentEditable={
                    <ContentEditable
                      className="min-h-full w-full resize-none border-none bg-transparent p-4 text-base text-left outline-none focus:ring-0"
                      spellCheck={true}
                    />
                  }
                  placeholder={
                    !isFocused ? (
                      <div className="pointer-events-none absolute left-4 top-4 text-sm text-muted-foreground">
                        Start typing your notes here... (Type "/" for commands)
                      </div>
                    ) : null
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                />
              </div>
            </ScrollArea>
            <EditorRefPlugin />
            <FocusPlugin onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} />
            <SlashCommandPlugin />
            <FloatingToolbarPlugin />
            <LinkPlugin />
            <ListPlugin />
            <CheckListPlugin />
            <HorizontalRulePlugin />
            <HistoryPlugin />
          </div>
        </LexicalComposer>
      </div>
    );
  };
